/**
 * Re-usable mapkit mock helper functions and types
 */

type InitOpts = {
  authorizationCallback: (
    done: (mapkitAuthenticationToken: string) => void,
  ) => void;
  language: string;
};

export type MapkitWindow = {
  mapkit: {
    Geocoder: () => void;
    init: (options: InitOpts) => void;
    Search: () => void;
  };
};

type LookupResult = {
  results: {
    coordinate: {
      latitude: string;
      longitude: string;
    };
  }[];
};

/**
 * An object containing the response from an autocomplete request.
 */
type SearchAutocompleteResponse = {
  /**
   * The query string used to perform the autocomplete request.
   */
  query: string;
  /**
   * The results from an autocomplete request.
   */
  results: SearchAutocompleteResult[];
};

/**
 * The result of an autocomplete query, including display lines and a coordinate.
 */
type SearchAutocompleteResult = {
  /**
   * Lines of text to display to the user in an autocomplete menu.
   */
  displayLines: string[];
};

/**
 * Options you can provide when performing a search.
 */
type SearchOptions = Record<string, unknown>;

type AutocompleteSearchCallback = (
  error: Error | null,
  data: SearchAutocompleteResponse,
) => void;

function Geocoder() {
  return {
    lookup: (
      /**
       * lookupValue
       *  "FORCE_ERROR" - forces an error to be returned by the lookup function
       *  "NO_RESULT" - forces no results to be returned by the lookup function
       */
      lookupValue: string,
      callback: (error: boolean, response?: LookupResult) => void,
      options: string,
    ) => {
      // force a set timeout to simulate network load time and to allow us to test / view the loading state
      setTimeout(() => {
        console.log("mock mapkit.Geocoder.lookup called", {
          lookupValue,
          options,
        });

        const error = lookupValue === "FORCE_ERROR";

        const response =
          lookupValue !== "NO_RESULT"
            ? {
                results: [
                  {
                    coordinate: {
                      latitude: "51.523772",
                      longitude: "-0.158538",
                    },
                  },
                ],
              }
            : {
                results: [],
              };
        callback(error, response);
      }, 500);
    },
  };
}

function Coordinate() {
  return {};
}

function CoordinateSpan() {
  return {};
}

function MarkerAnnotation() {
  return document.createElement("button");
}

let mapCurrent: HTMLDivElement | null = null;

function Map(ref: HTMLDivElement) {
  mapCurrent = ref;
  return {
    addAnnotation: () => true,
    annotations: [{ mock: "annotation" }],
    removeAnnotations: () => true,
    setCenterAnimated: () => true,
    showItems: (markerButtons: HTMLButtonElement[]) => {
      markerButtons.forEach((markerButton, index) => {
        if (mapCurrent) {
          const fragment = document.createDocumentFragment();
          const button = fragment.appendChild(document.createElement("button"));
          button.innerHTML = `Open the popup no ${index + 1}`;
          button.addEventListener("click", function () {
            markerButton.dispatchEvent(new Event("select"));
          });
          mapCurrent.appendChild(fragment);
          mapCurrent.appendChild(document.createElement("br"));
        }
      });
      return true;
    },
  };
}

function Search() {
  return {
    autocomplete: (
      query: string,
      callback: AutocompleteSearchCallback,
      options?: SearchOptions,
    ) => {
      console.log(options);
      const results: SearchAutocompleteResult = { displayLines: ["test line"] };
      const data: SearchAutocompleteResponse = {
        query: "test",
        results: [results],
      };
      callback(null, data);
    },
  };
}

export const mapkitMock = {
  Annotation: MarkerAnnotation,
  Coordinate: Coordinate,
  CoordinateSpan: CoordinateSpan,
  Geocoder: Geocoder,
  init: (options: InitOpts) => {
    console.log("mock mapkit.init called", options);
    options.authorizationCallback((mapkitAuthenticationToken) => {
      console.log("mock mapkit.init authorizationCallback called with: ", {
        mapkitAuthenticationToken,
      });
    });
  },
  Map: Map,
  MarkerAnnotation: MarkerAnnotation,
  Search: Search,
};
